import React, { useState, useEffect } from 'react';
import { Modal, FormInput, FormSelect, FormTextarea, Button, SearchableSelect } from '../../../components/ui';
import api from '../../../services/api';
import toast from 'react-hot-toast';

export default function SupportTicketModal({ isOpen, onClose, type, onSuccess, initialData }) {
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    status: 'open',
    customerName: '',
    phoneNumber: '',
    warrantyStatus: 'N/A'
  });

  // Populate data when editing
  useEffect(() => {
    if (initialData && isOpen) {
      setFormData({
        subject: initialData.subject || '',
        description: initialData.description || '',
        priority: initialData.priority || 'medium',
        status: initialData.status || 'open',
        customerName: initialData.customerName || '',
        phoneNumber: initialData.phoneNumber || '',
        warrantyStatus: initialData.warrantyStatus || 'N/A'
      });
    } else if (!initialData && isOpen) {
      setFormData({
        subject: '',
        description: '',
        priority: 'medium',
        status: 'open',
        customerName: '',
        phoneNumber: '',
        warrantyStatus: 'N/A'
      });
    }
  }, [initialData, isOpen]);

  // Fetch contacts for Customer Support
  useEffect(() => {
    if (type === 'Customer' && isOpen) {
      const fetchContacts = async () => {
        try {
          const res = await api.get('/verve-contacts');
          if (res.data?.success && res.data?.data) {
            setContacts(res.data.data.map(c => ({
              label: `${c.firstName} ${c.lastName || ''}`.trim(),
              value: `${c.firstName} ${c.lastName || ''}`.trim(),
              phone: c.phoneNumber
            })));
          }
        } catch (err) {
          console.error('Failed to load contacts for support ticket modal', err);
        }
      };
      fetchContacts();
    }
  }, [type, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomerChange = (val) => {
    const contact = contacts.find(c => c.value === val);
    if (contact) {
      setFormData(prev => ({ ...prev, customerName: contact.label, phoneNumber: contact.phone || '' }));
    } else {
      setFormData(prev => ({ ...prev, customerName: val, phoneNumber: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.description) {
      toast.error('Subject and description are required');
      return;
    }

    try {
      setLoading(true);
      const userStr = localStorage.getItem('blum_user');
      const user = userStr ? JSON.parse(userStr) : null;
      const createdBy = user ? user.name : 'Unknown User';

      const payload = {
        type,
        subject: formData.subject,
        description: formData.description,
        status: formData.status
      };

      if (!initialData) {
        payload.createdBy = createdBy;
      }

      if (type === 'Technical') {
        payload.priority = formData.priority;
      } else {
        payload.customerName = formData.customerName || 'N/A';
        payload.phoneNumber = formData.phoneNumber || '';
        payload.warrantyStatus = formData.warrantyStatus;
      }

      let res;
      if (initialData) {
        res = await api.patch(`/support/${initialData._id}`, payload);
      } else {
        res = await api.post('/support', payload);
      }

      if (res.data.success) {
        toast.success(res.data.message || `Ticket ${initialData ? 'updated' : 'created'} successfully`);
        onSuccess(res.data.data);
        onClose();
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.message || `Failed to ${initialData ? 'update' : 'create'} ticket`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? `Edit ${type} Ticket` : `New ${type} Support Ticket`}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormInput
          label="Subject"
          value={formData.subject}
          onChange={(e) => handleChange('subject', e.target.value)}
          placeholder="Brief description of the issue"
          required
        />
        
        {initialData && (
           <FormSelect
           label="Status"
           value={formData.status}
           onChange={(val) => handleChange('status', val)}
           options={[
             { value: 'open', label: 'Open' },
             { value: 'in_progress', label: 'In Progress' },
             { value: 'resolved', label: 'Resolved' },
             { value: 'closed', label: 'Closed' }
           ]}
         />
        )}
        
        {type === 'Customer' && (
          <div className="grid grid-cols-2 gap-4">
            <SearchableSelect
              label="Customer Name"
              value={formData.customerName}
              onChange={handleCustomerChange}
              options={contacts}
              placeholder="Search or add customer"
              allowCustom={true}
            />
            <FormInput
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={(e) => handleChange('phoneNumber', e.target.value)}
              placeholder="E.g. +91 9876543210"
            />
            <FormSelect
              label="Warranty Status"
              value={formData.warrantyStatus}
              onChange={(val) => handleChange('warrantyStatus', val)}
              options={[
                { value: 'Active', label: 'Active' },
                { value: 'Expired', label: 'Expired' },
                { value: 'N/A', label: 'N/A' }
              ]}
            />
          </div>
        )}

        {type === 'Technical' && (
          <FormSelect
            label="Priority"
            value={formData.priority}
            onChange={(val) => handleChange('priority', val)}
            options={[
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' }
            ]}
          />
        )}

        <FormTextarea
          label="Description"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Provide detailed information about the issue..."
          rows={4}
          required
        />

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={loading}>
            Submit Ticket
          </Button>
        </div>
      </form>
    </Modal>
  );
}
